import React, { useCallback, useEffect, useState } from 'react';

export interface RateAddOn {
  id: string;
  name: string;
  price: number;
}

export interface Rate {
  id: string;
  title: string;
  descriptionIntro?: string;
  description?: string;
  price?: number;
  addOns?: RateAddOn[];
  /** Consent text shown when this rate requires the user to confirm eligibility. */
  rateConfirmation?: string;
}

export interface RateCardProps {
  rate: Rate;
  isSelected: boolean;
  onSelect: () => void;
  /** Called whenever confirmation state changes for this card. */
  onConfirmedChange?: (confirmed: boolean) => void;
}

const RateCard: React.FC<RateCardProps> = ({
  rate,
  isSelected,
  onSelect,
  onConfirmedChange,
}) => {
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (!isSelected) {
      setConfirmed(false);
      onConfirmedChange?.(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelected]);

  const handleConfirm = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setConfirmed(true);
      onConfirmedChange?.(true);
    },
    [onConfirmedChange],
  );

  const requiresConfirmation = Boolean(rate.rateConfirmation);

  return (
    <div
      className={[
        'w-full flex-1 flex flex-col items-start rounded-[6px] bg-white',
        'shadow-[0px_3px_6px_-2px_rgba(12,17,29,0.12)] cursor-pointer border',
        isSelected ? 'border-[#002B66]' : 'border-transparent',
      ].join(' ')}
      onClick={onSelect}
    >
      <div className="w-full flex items-center justify-between px-5 pt-5 pb-2 font-semibold text-[16px] leading-[24px] text-[#002B66]">
        <span>{rate.title}</span>
        {isSelected && (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="10" cy="10" r="10" fill="#002B66" />
            <path d="M6 10.5L8.5 13L14 7" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>

      <div className="flex-1 flex flex-col justify-between px-5 pb-5 w-full overflow-hidden">
        {typeof rate.price === 'number' && (
          <div className="text-base font-semibold text-blue-700 mb-1">${rate.price}</div>
        )}

        {rate.addOns && rate.addOns.length > 0 && (
          <div>
            <div className="text-xs text-gray-500 font-medium mb-1">Add-ons available:</div>
            <ul className="list-disc pl-5">
              {rate.addOns.map((addOn) => (
                <li key={addOn.id} className="text-xs text-gray-700 flex justify-between">
                  <span>{addOn.name}</span>
                  <span className="text-gray-500">${addOn.price}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <p className="w-full text-[#475467] font-['Noto_Sans'] text-[14px] font-normal leading-[24px] break-words">
          {rate.description}
        </p>

        {isSelected && requiresConfirmation && !confirmed && (
          <>
            <div className="w-full border-t border-[#D0D5DD] mt-3 mb-3" />
            <div className="flex items-start gap-2" onClick={handleConfirm}>
              <div
                className="flex-shrink-0 mt-[3px] flex items-center justify-center cursor-pointer bg-white border border-[#667085] rounded-[4px] w-[16px] h-[16px]"
              />
              <label className="font-['Noto_Sans'] text-[14px] font-normal leading-[20px] text-[#475467] cursor-pointer select-none">
                {rate.rateConfirmation}
              </label>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RateCard;
